import * as PusherPushNotifications from '@pusher/push-notifications-web';

import React, { useEffect, useState } from 'react';

import AppBar from '@material-ui/core/AppBar';
import { Divider } from '@material-ui/core';
import Hidden from '@material-ui/core/Hidden';
import MaydayMessageProvider from '@/app/context/mayday-messages/MaydayMessageProvider';
import NavbarMobileToggleButton from '@/app/fuse-layouts/shared-components/NavbarMobileToggleButton';
import ResetLoginCodeModal from './schoolAdminTopNav/ResetLoginCodeModal';
import SchoolAdminTopNav from './schoolAdminTopNav/SchoolAdminTopNav';
import SuperAdminTopNav from './superAdminTopNav/superAdminTopNav';
import { ThemeProvider } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Topbar from './schoolAdminTopNav/Topbar';
import UserMenu from '@/app/fuse-layouts/shared-components/UserMenu';
import { stopBeamsClient } from '@/utils/utils';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const ToolbarLayout1 = () => {
	const config = useSelector(({ fuse }) => fuse.settings.current.layout.config);
	const toolbarTheme = useSelector(({ fuse }) => fuse.settings.toolbarTheme);
	const [unreadCount, setUnreadCount] = useState(0);
	const user = useSelector(({ auth }) => auth.user);
	const navigate = useNavigate();

	// useEffect(() => {
	// 	if (user?.data?.id && window.pendo) {
	// 		// prefix the id with the current environment to enable results filtering on the pendo portal
	// 		let currentEnv = '';
	// 		if (import.meta.env.VITE_ENV === 'development') {
	// 			currentEnv = 'dev-';
	// 		} else if (import.meta.env.VITE_ENV === 'stage') {
	// 			currentEnv = 'stage-';
	// 		}
	// 		window.pendo.initialize({
	// 			visitor: {
	// 				id: `${currentEnv}${user.data.email}`,
	// 				userId: user.data.id,
	// 				name: `${user.data.first_name} ${user.data.last_name}`,
	// 				email: user.data.email,
	// 				role: user.data.role,
	// 			},
	// 			account: {
	// 				id: `${currentEnv}${user.data.school.id}-${user.data.school.name}`,
	// 				name: user.data.school.name,
	// 			},
	// 		});
	// 	}
	// }, [user]);

	let beamsClient;
	let id;
	if (['super_admin', 'company_owner', 'school_admin', 'sub_admin'].includes(user.role[0])) {
		id = user.data.id;
	} else {
		id = user?.school?.admins ? user.school.admins[0].id : user?.school?.pivot?.user_id;
	}

	const startBeamsClient = () => {
		window.navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
			beamsClient = new PusherPushNotifications.Client({
				instanceId: import.meta.env.VITE_BEAMS_INSTANCE_ID,
				serviceWorkerRegistration,
			});

			beamsClient
				.getRegistrationState()
				.then((state) => {
					const states = PusherPushNotifications.RegistrationState;

					switch (state) {
						case states.PERMISSION_DENIED: {
							// Notifications are blocked
							// Show message saying user should unblock notifications in their browser
							break;
						}
						case states.PERMISSION_GRANTED_REGISTERED_WITH_BEAMS: {
							beamsClient
								.addDeviceInterest(`App.Models.User.${id}`)
								.then(() => beamsClient.getDeviceInterests())
								.catch(console.error);
							break;
						}
						case states.PERMISSION_GRANTED_NOT_REGISTERED_WITH_BEAMS:
						case states.PERMISSION_PROMPT_REQUIRED: {
							// Check if we've already done a reload for this session
							const hasReloaded = localStorage.getItem('beams-has-reloaded') === 'true';

							// Simple registration attempt - no retries
							const attemptBeamsStart = () => {
								navigator.serviceWorker.ready
									.then((registration) => registration.pushManager.getSubscription())
									.then(() => beamsClient.start())
									.then((client) => client.getDeviceId())
									.then(() => {
										// Try to add device interest
										return beamsClient
											.addDeviceInterest(`App.Models.User.${id}`)
											.then(() => {
												// Clear reload flag on success
												localStorage.removeItem('beams-has-reloaded');
												return beamsClient.getDeviceInterests();
											})
											.catch(() => {
												// Only reload if permission is granted and we haven't reloaded yet
												if (Notification.permission === 'granted') {
													if (hasReloaded) {
														// Already reloaded once, notifications likely work despite SDK reporting failure
														localStorage.removeItem('beams-has-reloaded');
													} else {
														localStorage.setItem('beams-has-reloaded', 'true');
														setTimeout(() => {
															window.location.reload();
														}, 1000);
													}
												}
											});
									})
									.catch(console.error);
							};

							// Start the attempt process
							attemptBeamsStart();
							break;
						}
						default: {
							throw new Error(`Unexpected Pusher SDK state: ${state}`);
						}
					}
				})
				.catch((e) => {
					console.error('Could not get registration state', e);
				});
		});
	};
	useEffect(() => {
		if (user.role.length > 0) {
			startBeamsClient();
		}

		// Monitor permission changes - only poll if permission is currently 'default'
		let permissionCheckInterval;
		if ('Notification' in window && user.role.length > 0 && Notification.permission === 'default') {
			permissionCheckInterval = setInterval(() => {
				const currentPermission = Notification.permission;

				// Stop polling once permission is no longer 'default'
				if (currentPermission !== 'default') {
					clearInterval(permissionCheckInterval);

					// Permission was granted, retry Beams initialization
					if (currentPermission === 'granted') {
						setTimeout(() => startBeamsClient(), 500); // Small delay to ensure APIs are ready
					}
				}
			}, 500); // Check every 500ms
		}

		return () => {
			stopBeamsClient(beamsClient);
			if (permissionCheckInterval) {
				clearInterval(permissionCheckInterval);
			}
		};
	}, [user]);

	return (
		<ThemeProvider theme={toolbarTheme}>
			<MaydayMessageProvider>
				<ResetLoginCodeModal setUnreadCount={setUnreadCount} />
				<AppBar
					className="flex z-10"
					color="default"
					id="fuse-toolbar"
					position="sticky"
					style={{ backgroundColor: '#fff', height: 81 }}
				>
					<Toolbar className="p-0 flex items-center h-full">
						{config.navbar.display && config.navbar.position === 'left' ? (
							<Hidden lgUp>
								<NavbarMobileToggleButton className="w-32 h-32 sm:w-48 sm:h-48 p-0 mx-10" />
							</Hidden>
						) : null}

						<button
							className="items-center lg:px-36"
							onClick={() => {
								if (user.role?.length) {
									if (['super_admin', 'company_owner', 'product_owner'].includes(user.role[0])) {
										navigate('/company');
									} else {
										navigate('/home');
									}
								}
							}}
							type="button"
						>
							<img
								alt=""
								className={`${user?.role?.length && 'cursor-pointer'} max-w-160 block px-16`}
								src="assets/images/logos/logo.png"
							/>
						</button>
						<Divider className=" my-16 hidden lg:block" flexItem orientation="vertical" />
						<div className="flex flex-1">
							<div className="hidden lg:block px-10 flex-1 min-w-0">
								{['school_admin', 'super_school_admin', 'sub_admin'].includes(user.role[0]) ? (
									<Topbar key={user} setUnreadCount={setUnreadCount} unreadCount={unreadCount} />
								) : null}
							</div>
						</div>
						<div className="flex justify-between items-center">
							{['super_admin', 'company_owner', 'product_owner'].includes(user.role[0]) ? (
								<SuperAdminTopNav />
							) : user.role.length ? (
								<SchoolAdminTopNav setUnreadCount={setUnreadCount} unreadCount={unreadCount} />
							) : (
								''
							)}
							{!['super_admin', 'company_owner'].includes(user.role[0]) ? (
								<Divider className="mx-16  md:ml-0 sm:mr-16 my-16" flexItem orientation="vertical" />
							) : null}
							{user.role.length ? <UserMenu /> : ''}
						</div>
					</Toolbar>
				</AppBar>
			</MaydayMessageProvider>
		</ThemeProvider>
	);
};

export default ToolbarLayout1;
